// ResearchPage — research/library link table. Visual preserved from original;
// content sourced from page_content row at slug `system-research`.

import { useQuery } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useApi } from "@/lib/api";
import { cn } from "@/lib/utils";

type ResearchRow = { path: string; title: string; subtitle?: string };

type ResearchContent = {
  kind: "research-v1";
  header: { eyebrow?: string; title: string; lede?: string };
  tableHeaders: { num: string; title: string; subtitle: string };
  rows: ResearchRow[];
};

const SLUG = "system-research";

function PageFrame({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-svh -mx-6 -my-8 px-6 py-6 sm:py-8 bg-stone-50">
      <div className="mx-auto max-w-5xl">{children}</div>
    </div>
  );
}

export function ResearchPage() {
  const navigate = useNavigate();
  const api = useApi();

  const { data, isLoading, error } = useQuery({
    queryKey: ["kb", SLUG],
    queryFn: async (): Promise<ResearchContent> => {
      const res = await api(`/api/kb/${SLUG}`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = (await res.json()) as { content: ResearchContent };
      return json.content;
    },
    staleTime: 60_000,
  });

  if (isLoading) {
    return (
      <PageFrame>
        <div className="flex items-center gap-2 p-2 text-sm text-stone-500">
          <Loader2 className="h-4 w-4 animate-spin" />
          טוען…
        </div>
      </PageFrame>
    );
  }

  if (error || !data) {
    return (
      <PageFrame>
        <div className="rounded-2xl border border-rose-300/60 bg-rose-50/40 p-5">
          <div className="text-sm font-semibold text-rose-700">לא ניתן לטעון את העמוד</div>
        </div>
      </PageFrame>
    );
  }

  return (
    <PageFrame>
      <header className="mb-6">
        {data.header.eyebrow && (
          <p className="text-[11px] font-semibold uppercase tracking-[0.28em] text-stone-500">
            {data.header.eyebrow}
          </p>
        )}
        <h1 className="mt-2 text-3xl sm:text-4xl font-bold tracking-[-0.02em] leading-tight text-stone-900">
          {data.header.title}
        </h1>
        {data.header.lede && (
          <p className="mt-3 text-base text-stone-600 max-w-xl leading-relaxed">
            {data.header.lede}
          </p>
        )}
      </header>

      <div className="rounded-[24px] bg-white overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-[11px] font-semibold uppercase tracking-[0.18em] text-stone-500">
              <th className="px-6 py-4 text-right font-semibold tabular-nums w-16">
                {data.tableHeaders.num}
              </th>
              <th className="px-6 py-4 text-right font-semibold">
                {data.tableHeaders.title}
              </th>
              <th className="px-6 py-4 text-right font-semibold">
                {data.tableHeaders.subtitle}
              </th>
            </tr>
          </thead>
          <tbody>
            {data.rows.map((g, idx) => (
              <tr
                key={g.path}
                onClick={() => navigate(g.path)}
                className={cn(
                  "border-t border-stone-200/70 cursor-pointer",
                  "hover:bg-stone-50 transition-colors",
                )}
              >
                <td className="px-6 py-4 align-middle font-bold tabular-nums text-stone-900">
                  {String(idx + 1).padStart(2, "0")}
                </td>
                <td className="px-6 py-4 align-middle">
                  <span className="text-base font-semibold text-stone-900 tracking-tight">
                    {g.title}
                  </span>
                </td>
                <td className="px-6 py-4 align-middle text-stone-600 max-w-[28rem]">
                  {g.subtitle && (
                    <span className="line-clamp-1">{g.subtitle}</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </PageFrame>
  );
}
